/**
 * Phase 2.1 — BOH / parts / customer bay-hold extensions: helper layer.
 *
 * Doctrine:
 *   - Bucket 1 §2.6 Hold Ownership vs Bay Occupancy
 *   - Bucket 2 §2.5.1 Bay-Hold Overrun (cause taxonomy)
 *   - Bucket 2 §2.6.1–§2.6.5 Pause AutoBump (vehicle-in-bay only)
 *   - Bucket 5 §5.6.5 Break Line vs Pause AutoBump (no double-count)
 *
 * Why this module exists
 * ----------------------
 * Tech OptiTime must remain technician execution time only. Bay occupancy
 * extensions may extend OptiFinish only when they physically keep the
 * vehicle occupying a bay/lane. Today the scheduling write path
 * (`supabase/functions/_shared/bayOccupancyScheduling.ts`) is wired only
 * for FOH/advisor custody (tracked via `custody_events`, addon-gated). The
 * other Bucket 2 hold owners — BOH/technician, parts, customer approval,
 * manager, vendor, sublet, machine — are captured at the `pause_reason`
 * level but have no canonical classification helper. Adoption sites that
 * want to ask "is this hold bay-blocking right now?" derive it ad-hoc.
 *
 * This module gives callers a single canonical answer.
 *
 * Phase 2.1 scope: ACCESSOR + TESTS ONLY.
 *   - Additive. Does NOT modify any OptiFinish writer, cascade behavior,
 *     AutoBump, OptiAssign, or Due Out enforcement.
 *   - Does NOT alter Tech OptiTime calculation anywhere.
 *   - No DB enum, no migration, no new column.
 *   - No `pause_reason` mutation behavior changes.
 *   - Pause AutoBump itself (Phase 2.2) is NOT added here.
 *
 * The module deliberately leaves the FOH/advisor custody path alone.
 * That path uses `custody_events` (kick_to_foh / foh_accept / kick_to_boh
 * / boh_accept), not `pause_reason`. The two systems coexist:
 *   - FOH bay-hold: tracked via custody_events; observed extension
 *     already lands on `tickets.opti_finish` under the
 *     `BAY_OCCUPANCY_AFFECTS_SCHEDULING` addon.
 *   - BOH / parts / customer / manager / vendor / sublet bay-hold:
 *     classified by THIS module via `pause_reason`. Future slices may
 *     wire these into the scheduling write path the same way Slice 5D-13
 *     wired FOH.
 *
 * Builds on Phase 1.1 / 1.3:
 *   - `getProductionState(ticket)` — single source of truth for state.
 *   - `isBayBlockingProductionState(state)` — true for active_in_bay /
 *     held_in_bay; this module ANDs with the hold-reason check so a
 *     Held-in-Parking ticket with the same pause reason never extends
 *     bay-based OptiFinish.
 */

import {
  getProductionState,
  isBayBlockingProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Hold-owner taxonomy
// =============================================================================

/**
 * Canonical hold owners enumerated in Bucket 2 §2.5.1. String literals
 * (not an enum) to match the project style established by `ProductionState`
 * and `BoardIssueType` — tree-shakeable, audit-friendly, obvious in
 * DevTools.
 *
 *   - `parts`    — waiting on a part
 *   - `customer` — waiting on customer approval / signature
 *   - `boh`      — BOH/technician hold (diagnostics, blocked-by-other-job)
 *   - `foh`      — advisor / front-of-house hold. Today this owner is
 *                  observed via the `custody_events` table, NOT via
 *                  `pause_reason`. Provided here for completeness so
 *                  downstream consumers can normalise both sources.
 *   - `manager`  — manager decision hold
 *   - `vendor`   — vendor / sublet work pending offsite
 *   - `sublet`   — alias for vendor in some shops; kept distinct here so
 *                  future analytics can split them
 *   - `machine`  — machine-driven operation (A/C, programming, cure/dwell)
 *   - `break`    — technician break (NOT a bay-blocking owner; see Bucket
 *                  5 §5.6.5 — Break Line handles scheduled break time,
 *                  Pause AutoBump must not double-count)
 *   - `unknown`  — pause_reason was missing / 'Other' / unrecognised
 */
export type BayHoldOwner =
  | 'parts'
  | 'customer'
  | 'boh'
  | 'foh'
  | 'manager'
  | 'vendor'
  | 'sublet'
  | 'machine'
  | 'break'
  | 'unknown';

/**
 * Frozen list of all canonical hold owners. Useful for exhaustiveness
 * checks and for tests that enumerate owners.
 */
export const BAY_HOLD_OWNERS = Object.freeze([
  'parts',
  'customer',
  'boh',
  'foh',
  'manager',
  'vendor',
  'sublet',
  'machine',
  'break',
  'unknown',
] as const) satisfies ReadonlyArray<BayHoldOwner>;

// =============================================================================
// Classification
// =============================================================================

/**
 * Classify a `pause_reason` string into a canonical `BayHoldOwner`.
 *
 * Today `pause_reason` is the user-facing pause taxonomy
 * (`src/constants/pauseReasons.ts`):
 *
 *   'Waiting for parts'      → 'parts'
 *   'Customer approval'      → 'customer'
 *   'Tech on break'          → 'break'
 *   'Awaiting diagnostics'   → 'boh'
 *   'Blocked by other job'   → 'boh'
 *   'Other'                  → 'unknown'
 *   'break_failsafe'         → 'break'   (deprecated system reason)
 *
 * Defensive matching: case-insensitive trim. Unrecognised strings fall
 * back to `'unknown'` so a future pause_reason addition that isn't
 * mapped here can't accidentally be treated as bay-blocking.
 *
 * Note: `foh`, `manager`, `vendor`, `sublet`, `machine` are NOT currently
 * surfaced through `pause_reason`. They have their own provenance:
 *   - FOH custody → `custody_events` table
 *   - Manager / vendor / sublet / machine → not yet first-class in the
 *     enum; map to 'unknown' for now. A future slice may extend
 *     `PAUSE_REASONS` or add a separate `hold_owner` column.
 */
export function getPauseHoldOwner(
  pauseReason: string | null | undefined,
): BayHoldOwner {
  if (pauseReason == null) return 'unknown';
  const r = String(pauseReason).trim().toLowerCase();
  if (r === '') return 'unknown';

  // Current user-facing enum (src/constants/pauseReasons.ts).
  if (r === 'waiting for parts') return 'parts';
  if (r === 'customer approval') return 'customer';
  if (r === 'tech on break') return 'break';
  if (r === 'awaiting diagnostics') return 'boh';
  if (r === 'blocked by other job') return 'boh';
  if (r === 'other') return 'unknown';

  // Stage D — the one Pause answer (pauseIntent.ts). An advisor need is the
  // front of house's to resolve; technical help is still the shop's (BOH).
  if (r === 'estimate needed') return 'foh';
  if (r === 'parts or pricing needed') return 'foh';
  if (r === 'advisor question') return 'foh';
  if (r === 'technical help needed') return 'boh';

  // System / deprecated reasons.
  if (r === 'break_failsafe') return 'break';

  // Defensive: unrecognised → unknown. Avoids accidentally treating a
  // future pause_reason as bay-blocking by default.
  return 'unknown';
}

/**
 * Is this hold owner "bay-blocking in principle"?
 *
 * "In principle" means: if the vehicle remains in the bay, this owner's
 * hold should extend OptiFinish. The actual extension decision also
 * requires the ticket to be physically bay-blocking — see
 * `shouldHoldExtendOptiFinish`.
 *
 * Bay-blocking owners (Bucket 2 §2.5.1):
 *   parts, customer, boh, foh, manager, vendor, sublet, machine
 *
 * Not bay-blocking:
 *   - `break` — Break Line handles scheduled break time; Pause AutoBump
 *     must not double-count (Bucket 5 §5.6.5, Bucket 2 §2.6.5).
 *   - `unknown` — conservative default; callers can opt-in via shop
 *     setting if they want unknown-reason holds to extend OptiFinish.
 */
export function isBayBlockingHoldOwner(owner: BayHoldOwner): boolean {
  switch (owner) {
    case 'parts':
    case 'customer':
    case 'boh':
    case 'foh':
    case 'manager':
    case 'vendor':
    case 'sublet':
    case 'machine':
      return true;
    case 'break':
    case 'unknown':
      return false;
  }
}

/**
 * Convenience: classify a `pause_reason` and check if its owner is
 * bay-blocking in one call.
 *
 * Equivalent to `isBayBlockingHoldOwner(getPauseHoldOwner(pauseReason))`.
 */
export function isBayBlockingHoldReason(
  pauseReason: string | null | undefined,
): boolean {
  return isBayBlockingHoldOwner(getPauseHoldOwner(pauseReason));
}

// =============================================================================
// Combined decision: should this hold extend OptiFinish right now?
// =============================================================================

/**
 * Should this ticket's current hold extend bay-based OptiFinish?
 *
 * Returns TRUE if and only if BOTH:
 *   - the ticket is in a bay-blocking production state
 *     (`active_in_bay` or `held_in_bay`), AND
 *   - the hold owner (derived from `pause_reason`) is bay-blocking
 *     in principle.
 *
 * This is the canonical predicate for Bucket 1 §2.6: "Only holds that
 * physically keep the vehicle occupying a bay/lane may extend
 * OptiFinish."
 *
 * Held-in-Parking always returns FALSE here, even when the hold reason
 * (parts, customer, etc.) would normally be bay-blocking — Bucket 3
 * §3.4.6: "It does not consume current bay occupancy."
 *
 * Terminal states (deleted, archived, picked_up, completed_awaiting_pickup)
 * always return FALSE — none of them block a bay.
 *
 * The `pause_reason` argument is permissive: missing / null / 'Other'
 * resolves to `unknown` and does NOT extend. Callers should explicitly
 * pass an owner string if they want a non-pause_reason-sourced owner
 * (e.g. an FOH owner derived from `custody_events`) treated as
 * bay-blocking — Phase 2.1 leaves that integration for a later slice.
 */
export function shouldHoldExtendOptiFinish(
  ticket: ProductionStateTicketShape,
  pauseReason?: string | null,
): boolean {
  const stateBayBlocking = isBayBlockingProductionState(getProductionState(ticket));
  if (!stateBayBlocking) return false;
  const reasonToCheck = pauseReason !== undefined ? pauseReason : ticket.pause_reason;
  return isBayBlockingHoldReason(reasonToCheck);
}

/**
 * Return the hold-owner classification that should extend OptiFinish for
 * this ticket right now, or `null` if no extension applies.
 *
 * Useful for callers that need to attribute the extension to a specific
 * owner (audit rows, display badges). The classification is sourced from
 * `pause_reason` today; future slices may consult `custody_events` for
 * FOH and `hold_owner` (if added) for other owners.
 */
export function getBayHoldExtensionOwner(
  ticket: ProductionStateTicketShape,
  pauseReason?: string | null,
): BayHoldOwner | null {
  if (!shouldHoldExtendOptiFinish(ticket, pauseReason)) return null;
  const reasonToCheck = pauseReason !== undefined ? pauseReason : ticket.pause_reason;
  return getPauseHoldOwner(reasonToCheck);
}
